import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './tufts-yext-item.css.lit';
import '../tufts-hero/tufts-hero';
import '../tufts-container/tufts-container';
import '../tufts-side-by-side/tufts-side-by-side';
import '../tufts-grid/tufts-grid';
import '../tufts-icon-link/tufts-icon-link';
import '../tufts-icon-link-group/tufts-icon-link-group';
import '../tufts-clinical-trials-card/tufts-clinical-trials-card';
import '../tufts-icon-with-paragraph/tufts-icon-with-paragraph';
import '../tufts-breadcrumbs/tufts-breadcrumbs';
import '../tufts-leaflet-map/tufts-leaflet-map';
import '../tufts-social-links/tufts-social-links';
import '../tufts-video-lite/tufts-video-lite';
import '../tufts-tabs/tufts-tab-group/tufts-tab-group';
import '../tufts-tabs/tufts-tab-panel/tufts-tab-panel';
import '../tufts-tabs/tufts-tab/tufts-tab';
import '../tufts-yext-location/tufts-yext-location';
import '../tufts-provider-qualifications/tufts-provider-qualifications';
import '../tufts-rating/tufts-rating';
import { initialState, Task } from '@lit-labs/task';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
  defaultImageTemplate,
  transformImageUrl,
} from '../tufts-yext/image-template';
import type {
  verticalSearchResponseStructure,
  YextLocation,
  EntityDataStructure,
  PubMedSearchResponse,
  institutionStructure,
  RatingResponse,
  CommentData,
} from '../tufts-yext/tufts-yext-types';
import {
  formatPhone,
  addTargetBlankToAnchorTags,
} from '../tufts-yext/tufts-yext-format';
import { ResizeController } from '../../controllers/resize-controller';
import { repeat } from 'lit/directives/repeat.js';

/**
 * The Yext Item component.
 * @element tufts-yext-item
 */
@customElement('tufts-yext-item')
export class TuftsYextItem extends OutlineElement {
  /* eslint-disable no-console */
  static styles: CSSResultGroup = [componentStyles];
  resizeController: ResizeController = new ResizeController(this, {});

  @property({ type: String, reflect: true, attribute: 'url-id' })
  urlID: string;

  @state() entityId = '';
  @state() openSchedulingFromSearch = false;
  @state() dataFromProfileNewsArticles: string;
  @state() dataFromClinicalTrials: string;
  openScheduling = false;
  @state() locationsArray: YextLocation[] | null = null;

  urlHref = 'https://cdn.yextapis.com/v2/accounts';
  accountId = 'me';
  contentType = 'entities';
  // apiKey = process.env.apiKey || '';
  apiKey = 'f70a1166dcbde8731713f74b5d21b86f';
  apiVersion = '20230301';
  urlBase = `${this.urlHref}/${this.accountId}/${this.contentType}`;
  currentDomainURL = window.location.protocol + '//' + window.location.hostname;

  lastFetchTime: null | number;

  entityNotFound = false;
  @state() isPubMedDataReady = false;

  @state()
  entities: [];

  npiProvider: string | undefined;
  totalPages = 1;
  currentPage = 1;
  @state() dataComments: CommentData[] = [];

  citationsHtml: {
    AU: string[];
    T1: string;
    JF: string;
    Y1: string;
    UR: string;
  }[] = [];
  connectedCallback() {
    super.connectedCallback();
    // Add the event listener when the component is connected to the DOM
    window.addEventListener('load', this.handleLoadEvent);
  }

  _apiTask = new Task(
    this,
    async () => {
      const startTime = performance.now();
      await this.getUrlIDFromURLParams();
      if (!this.entityId) {
        return initialState;
      }
      try {
        const entityUrl = this.prepareRequestUrl(this.entityId);
        const response = await fetch(entityUrl);
        const data = await response.json();
        if (Object.keys(data.response).length !== 0) {
          const endTime = performance.now();
          this.lastFetchTime = (endTime - startTime) / 1000;

          this.addLocation(data.response);
          this.fetchAdditionalProfileLocations(data.response);
          // @todo: temporarily hide ratings
          // this.createRating(data.response);
          this.fetchPublicationPubMed(data.response);
          this.fetchProfileNewsArticles(data.response);
          this.fetchProfileClinicalTrials(data.response);

          return data;
        }
        this.entityNotFound = true;
        return data;
      } catch {
        throw new Error(`Failed to fetch "${this.urlID}"`);
      }
    },
    () => [this.entities]
  );

  async fetchPublicationPubMed(data: EntityDataStructure) {
    // If the c_oRCIDID field does not exist or is empty, do not make any call
    if (!data.c_oRCIDID) {
      return;
    }
    const baseUrl =
      'https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi';
    const apiIdListBase =
      'https://api.ncbi.nlm.nih.gov/lit/ctxp/v1/pubmed/?format=ris&id=';

    const url = `${baseUrl}?db=pubmed&term=${encodeURIComponent(
      data.c_oRCIDID
    )}&retmode=json&retmax=5&sort=date`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: PubMedSearchResponse = await response.json();
      const citations = [];

      for (const id of data.esearchresult.idlist) {
        const apiIdListUrl = `${apiIdListBase}${id}`;
        try {
          const risResponse = await fetch(apiIdListUrl);
          if (!risResponse.ok) {
            throw new Error(`HTTP error! status: ${risResponse.status}`);
          }
          const risText = await risResponse.text();
          const citationData = this.parseRIS(risText);
          citations.push(citationData);
        } catch (error) {
          console.error('Error fetching citation data:', error);
        }
        // Implement rate limiting or use a more sophisticated approach
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      this.citationsHtml = [...this.citationsHtml, ...citations];
      this.isPubMedDataReady = true;
      this.requestUpdate();
    } catch (error) {
      console.error('Error fetching PubMed data:', error);
    }
  }

  parseRIS(risText: string): {
    AU: string[];
    T1: string;
    JF: string;
    Y1: string;
    UR: string;
  } {
    const lines = risText.split('\n');
    const result: {
      AU?: string[];
      T1?: string;
      JF?: string;
      Y1?: string;
      UR?: string;
    } = {};

    for (const line of lines) {
      const code = line.substring(0, 2);
      const value = line.substring(6).trim();

      switch (code) {
        case 'AU':
          if (!result['AU']) result['AU'] = [];
          result['AU'].push(value);
          break;
        case 'T1':
          result['T1'] = value;
          break;
        case 'JF':
          result['JF'] = value;
          break;
        case 'Y1':
          result['Y1'] = this.formatDatePubMed(value);
          break;
        case 'UR':
          result['UR'] = value;
          break;
      }
    }

    return result as {
      AU: string[];
      T1: string;
      JF: string;
      Y1: string;
      UR: string;
    };
  }
  formatDatePubMed(input: string): string {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const [year, month, day] = input.split('/');

    return day
      ? `${months[+month - 1]} ${day} ${year}`
      : `${months[+month - 1]} ${year}`;
  }

  addLocation(jsonResponse: EntityDataStructure) {
    if (
      jsonResponse.yextDisplayCoordinate &&
      jsonResponse.yextDisplayCoordinate.latitude &&
      jsonResponse.yextDisplayCoordinate.longitude &&
      !jsonResponse.closed
    ) {
      const addressString = `${jsonResponse.address.line1 || ''} ${jsonResponse.address.line2 || ''} ${jsonResponse.address.city || ''} ${jsonResponse.address.region || ''} ${jsonResponse.address.postalCode || ''}`;
      const addressCityStateString = `${jsonResponse.address.city || ''}, ${jsonResponse.address.region || ''} ${jsonResponse.address.postalCode || ''}`;
      const titleOrAddress = jsonResponse.officeName || addressString;
      const mainNumber =
        formatPhone(jsonResponse.mainPhone) || jsonResponse.mainPhone;
      const extantLocations = this.locationsArray?.length
        ? [...this.locationsArray]
        : [];
      this.locationsArray = [...extantLocations];
      this.locationsArray.push({
        latitude: jsonResponse.yextDisplayCoordinate.latitude,
        longitude: jsonResponse.yextDisplayCoordinate.longitude,
        title: titleOrAddress,
        address: jsonResponse.address.line1,
        address_2:
          jsonResponse.address.line2 !== ''
            ? jsonResponse.address.line2
            : undefined,
        address_3: addressCityStateString,
        number: mainNumber,
      });
    }
  }

  updatePageTitle(doctorsName: string) {
    // Find the <title> element
    const titleElement = document.querySelector('title');

    // Check if the <title> element exists
    if (titleElement) {
      // Get the current text content of the <title> element
      const currentTitleText = titleElement.textContent;

      // Check if there is content in the <title> element
      if (currentTitleText) {
        // Replace the part before the '|' character with the modified name
        const modifiedTitleText = currentTitleText.replace(
          /^[^|]+/,
          doctorsName + ' '
        );

        // Update the text content of the <title> element
        titleElement.textContent = modifiedTitleText;
      }
    }
  }

  breadcrumbsTemplate(): TemplateResult {
    return html`
      <tufts-breadcrumbs>
        <nav class="breadcrumb">
          <ol slot="breadcrumb">
            <li>
              <a href="/">Home</a>
            </li>
            <li>
              <a href="/get-care">Get Care</a>
            </li>
            <li>
              <a href="/find-a-doctor">Find a Doctor</a>
            </li>
          </ol>
        </nav>
      </tufts-breadcrumbs>
    `;
  }

  generateEducationTemplate(
    educationArray: institutionStructure[] = [],
    title: string
  ) {
    if (educationArray.length === 0) {
      return null;
    }

    return html`
      <div class="content-wrapper">
        <span class="content-title"> ${title}</span>
        <div class="content-details-wrapper">
          ${repeat(
            educationArray,
            education => education.institutionName,
            education => html`<span>${education.institutionName}</span>`
          )}
        </div>
      </div>
    `;
  }

  publicationAndNationalPresentationTemplate(
    pubMedSearchUrl: string,
    name: string
  ) {
    return this.isPubMedDataReady
      ? html` <div>
          <div class="content-details-wrapper">
            ${html`
              <a class="link-pubmed" href="${pubMedSearchUrl}" target="_blank"
                >View all of ${name}'s publications on PubMed</a
              >
              ${unsafeHTML(
                this.citationsHtml
                  ?.map(
                    (citation, index) =>
                      `<p>${index + 1}. ${citation.AU.join(', ')}. <strong>${
                        citation.T1
                      }</strong>; ${citation.Y1}</p>`
                  )
                  .join('')
              )}
            `}
          </div>
        </div>`
      : undefined;
  }
  scrollToRating() {
    const ratingElement = this.shadowRoot?.querySelector('#rating');
    if (ratingElement) {
      const childHeading = ratingElement.querySelector('tufts-heading');
      ratingElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (childHeading) {
        // Use setTimeout to delay focus, preventing interference with smooth scrolling.
        setTimeout(() => {
          childHeading.focus();
        }, 1000);
      }
    }
  }
  handleLoadEvent = () => {
    setTimeout(() => {
      const iframes = document.querySelectorAll('iframe[id^="MyChartIframe"]');
      if (iframes.length > 1) {
        // If more than one iframe exists, remove the additional ones to ensure only one is present.
        for (let i = 1; i < iframes.length; i++) {
          iframes[i].remove();
        }
      }
    }, 1000);
  };

  displayEntity(response: EntityDataStructure) {
    if (this.entityNotFound) {
      return html`<h1>The requested profile could not be found</h1>`;
    }
    const formattedMainPhone = formatPhone(response.mainPhone);

    if (response.name) {
      this.updatePageTitle(response.name);
    }
    const hiddenValue = false;

    // prevent dispatchs setOpenSchedulingData
    if (
      response.c_openScheduling &&
      response.c_openSchedulingVisitTypeId &&
      !this.openScheduling
    ) {
      this.setOpenSchedulingData(
        response.npi,
        response.c_openSchedulingVisitTypeId,
        response.c_openSchedulingDepartmentId
      );
    }

    // New Training + Education data field.
    const educationTypes = {
      medical_schoool: response.educationList?.filter(
        item => item.type === 'MEDICAL_SCHOOL'
      ),
      residency: response.educationList?.filter(
        item => item.type === 'RESIDENCY'
      ),
      fellowship: response.educationList?.filter(
        item => item.type === 'FELLOWSHIP'
      ),
      internship: response.educationList?.filter(
        item => item.type === 'INTERNSHIP'
      ),
    };
    // Initialize the PubMed  search URL
    let pubMedSearchUrl = 'https://pubmed.ncbi.nlm.nih.gov/';

    // If the response contains an ORCID ID, use it for the PubMed search.
    if (response.c_oRCIDID) {
      pubMedSearchUrl = `https://pubmed.ncbi.nlm.nih.gov/?term=${response.c_oRCIDID}`;
    }

    return html`
      <tufts-container padding="space-0">
        ${this.breadcrumbsTemplate()}
      </tufts-container>
      <tufts-hero
        pulse-orientation="top-right"
        vertical-trigger-width="550"
        ?pre-cta-expanded=${this.openSchedulingFromSearch}
      >
        ${response.name
          ? html`<div slot="title">
              <h1>${response.name}</h1>
              ${response.c_preferredPronouns
                ? html` <span>${response.c_preferredPronouns}</span> `
                : undefined}
            </div>`
          : undefined}
        ${response.c_specialtyName
          ? html`<div slot="sub-title">
              <h4>${response.c_specialtyName.join(', ')}</h4>
            </div>`
          : undefined}
        ${response.mainPhone
          ? html`<div slot="cta">
              <div class="container-cta-doctor full-link-in-slot">
                <tufts-button button-variant="compact reversed" icon-type="">
                  <a href="tel:${response.mainPhone}"
                    >${'Call ' + formattedMainPhone}</a
                  >
                </tufts-button>
              </div>
            </div>`
          : undefined}
        ${this.openScheduling
          ? html`<div slot="pre-cta">
              <slot name="open-scheduling"> </slot>
            </div> `
          : undefined}
        ${this.generateExpertiseTemplate(response)}
        ${response.c_clinicalTitles || response.c_academicTitles
          ? html` <div slot="body">
              <div class="body-container">
                ${response.c_clinicalTitles
                  ? html` <strong>Clinical titles</strong>
                      ${unsafeHTML(
                        response.c_clinicalTitles
                          .map(clinicalTitle => clinicalTitle)
                          .join('<br>')
                      )}
                      <br /><br />`
                  : null}
                ${response.c_academicTitles
                  ? html`<strong>Academic titles</strong>
                      ${response.c_academicTitles
                        ? html`${unsafeHTML(
                            response?.c_academicTitles
                              .map(title => title)
                              .join('<br>')
                          )}`
                        : null}`
                  : null}
              </div>
            </div>`
          : undefined}
        <div slot="image">
          ${response.headshot?.url
            ? html`
                <img
                  loading="lazy"
                  src="${transformImageUrl(response.headshot.url, 698, 465)}"
                  alt="${ifDefined(response.name)}"
                />
              `
            : html` ${defaultImageTemplate()}`}
        </div>
        ${response.rating
          ? html`<div class="rating-container">
              <div
                class="average-rating"
                style="--percent-rating: calc(${response.rating}/5*100%);"
              >
                <span class="visually-hidden"
                  >Average rating score:${response.rating} out of 5</span
                >
              </div>
              <span
                ><strong>${response.rating}</strong> (${response.rating_count}
                ratings)</span
              >

              ${response.question_ratings &&
              response.question_ratings.length > 0
                ? html`<button
                    type="button"
                    class="btn medium link"
                    @click="${this.scrollToRating}"
                  >
                    See all reviews
                  </button> `
                : null}
            </div> `
          : null}
        <slot></slot>
      </tufts-hero>

      <tufts-container>
        <tufts-side-by-side side="right">
          <tufts-icon-link-group max-columns="2" slot="side-content">
            ${this.templateCTA(response)}
          </tufts-icon-link-group>
          ${response.acceptingNewPatients ||
          response.c_openScheduling ||
          response.languages ||
          response.c_lGBTQIAHealth
            ? html`<tufts-clinical-trials-card
                border=""
                slot="main-content"
                fit-content
              >
                ${response.acceptingNewPatients
                  ? html`<div slot="icon-with-text">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.6016 16.5999L17.6516 9.5499L16.2516 8.1499L10.6016 13.7999L7.75156 10.9499L6.35156 12.3499L10.6016 16.5999Z"
                          fill="#428FEC"
                        />
                        <path
                          d="M21.21 8.1C20.69 6.88 19.97 5.83 19.07 4.93C18.17 4.03 17.11 3.32 15.9 2.79C14.68 2.27 13.38 2 12 2C10.62 2 9.32 2.26 8.1 2.79C6.88 3.32 5.83 4.03 4.93 4.93C4.03 5.83 3.32 6.89 2.79 8.1C2.26 9.32 2 10.62 2 12C2 13.38 2.26 14.68 2.79 15.9C3.31 17.12 4.03 18.18 4.93 19.07C5.83 19.96 6.89 20.68 8.1 21.21C9.32 21.74 10.62 22 12 22C13.38 22 14.68 21.74 15.9 21.21C17.12 20.69 18.18 19.97 19.07 19.07C19.97 18.17 20.68 17.11 21.21 15.9C21.74 14.68 22 13.38 22 12C22 10.62 21.74 9.32 21.21 8.1ZM17.67 17.68C16.12 19.23 14.23 20.01 11.99 20.01C9.75 20.01 7.87 19.23 6.32 17.68C4.77 16.13 3.99 14.24 3.99 12.01C3.99 9.78 4.77 7.89 6.32 6.34C7.87 4.79 9.76 4.01 11.99 4.01C14.22 4.01 16.11 4.79 17.67 6.34C19.22 7.89 19.99 9.78 19.99 12.01C19.99 14.24 19.22 16.13 17.67 17.68Z"
                          fill="#00308C"
                        />
                      </svg>
                      Accepting new patients
                    </div>`
                  : undefined}
                ${response.c_openScheduling
                  ? html`<div slot="icon-with-text">
                      <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.07 20H5C4.45 20 3.98 19.8 3.59 19.41C3.2 19.02 3 18.55 3 18V7C3 6.45 3.2 5.98 3.59 5.59C3.98 5.2 4.45 5 5 5H6V3H8V5H14V3H16V5H17C17.55 5 18.02 5.2 18.41 5.59C18.8 5.98 19 6.45 19 7V13.07C18.83 13.04 18.67 13.02 18.51 13.01C18.35 13.01 18.18 13 18 13C17.82 13 17.65 13 17.49 13.01C17.33 13.01 17.17 13.04 17 13.07V10H5V18H12.07C12.04 18.17 12.02 18.33 12.01 18.49C12.01 18.65 12 18.82 12 19C12 19.18 12 19.35 12.01 19.51C12.01 19.67 12.04 19.83 12.07 20Z"
                          fill="#00308C"
                        />
                        <path
                          d="M17 23V20H14V18H17V15H19V18H22V20H19V23H17Z"
                          fill="#428FEC"
                        />
                      </svg>
                      Online scheduling
                    </div>`
                  : undefined}
                ${response.c_employer && hiddenValue
                  ? html`<div slot="icon-with-text">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.5 11.9498C12.98 11.4198 13.35 10.8098 13.61 10.1298C13.87 9.44982 14 8.73982 14 8.00982C14 7.27982 13.87 6.56982 13.61 5.88982C13.35 5.20982 12.98 4.59982 12.5 4.06982C13.5 4.19982 14.33 4.63982 15 5.38982C15.67 6.13982 16 7.00982 16 8.00982C16 9.00982 15.67 9.88982 15 10.6298C14.33 11.3698 13.5 11.8198 12.5 11.9498Z"
                          fill="#428FEC"
                        />
                        <path
                          d="M18.0008 20.0001V17.0001C18.0008 16.4001 17.8708 15.8301 17.6008 15.2901C17.3308 14.7501 16.9808 14.2701 16.5508 13.8501C17.4008 14.1501 18.1908 14.5401 18.9108 15.0101C19.6308 15.4901 20.0008 16.1501 20.0008 17.0001V20.0001H18.0008Z"
                          fill="#428FEC"
                        />
                        <path
                          d="M20 13V11H18V9H20V7H22V9H24V11H22V13H20Z"
                          fill="#428FEC"
                        />
                        <path
                          d="M10.83 5.17C10.05 4.39 9.1 4 8 4C6.9 4 5.96 4.39 5.17 5.17C4.38 5.95 4 6.9 4 8C4 9.1 4.39 10.04 5.17 10.83C5.95 11.62 6.9 12 8 12C9.1 12 10.04 11.61 10.83 10.83C11.61 10.05 12 9.1 12 8C12 6.9 11.61 5.96 10.83 5.17ZM9.42 9.41C9.03 9.8 8.56 10 8.01 10C7.46 10 6.99 9.8 6.6 9.41C6.21 9.02 6.01 8.55 6.01 8C6.01 7.45 6.21 6.98 6.6 6.59C6.99 6.2 7.46 6 8.01 6C8.56 6 9.03 6.2 9.42 6.59C9.81 6.98 10.01 7.45 10.01 8C10.01 8.55 9.81 9.02 9.42 9.41Z"
                          fill="#00308C"
                        />
                        <path
                          d="M15.56 15.64C15.27 15.17 14.88 14.8 14.4 14.55C13.37 14.03 12.32 13.65 11.25 13.39C10.18 13.13 9.1 13 8 13C6.9 13 5.82 13.13 4.75 13.39C3.68 13.65 2.63 14.04 1.6 14.55C1.12 14.8 0.73 15.16 0.44 15.64C0.15 16.12 0 16.64 0 17.2V20H16V17.2C16 16.63 15.85 16.11 15.56 15.64ZM14 18H2V17.2C2 17.02 2.05 16.85 2.14 16.7C2.23 16.55 2.35 16.43 2.5 16.35C3.4 15.9 4.31 15.56 5.22 15.34C6.14 15.12 7.06 15 8 15C8.94 15 9.86 15.11 10.78 15.34C11.7 15.57 12.6 15.9 13.5 16.35C13.65 16.43 13.77 16.55 13.86 16.7C13.95 16.85 14 17.02 14 17.2V18Z"
                          fill="#00308C"
                        />
                      </svg>
                      ${response.c_employer}
                    </div>`
                  : undefined}
                ${response.languages
                  ? html`<div slot="icon-with-text">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 -4 20 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 20C8.61667 20 7.31667 19.7373 6.1 19.212C4.88333 18.6873 3.825 17.975 2.925 17.075C2.025 16.175 1.31267 15.1167 0.788 13.9C0.262667 12.6833 0 11.3833 0 10C0 8.61667 0.262667 7.31667 0.788 6.1C1.31267 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.31233 6.1 0.787C7.31667 0.262333 8.61667 0 10 0C11.3833 0 12.6833 0.262333 13.9 0.787C15.1167 1.31233 16.175 2.025 17.075 2.925C17.975 3.825 18.6873 4.88333 19.212 6.1C19.7373 7.31667 20 8.61667 20 10C20 11.3833 19.7373 12.6833 19.212 13.9C18.6873 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6873 13.9 19.212C12.6833 19.7373 11.3833 20 10 20ZM9 17.95V16C8.45 16 7.97933 15.8043 7.588 15.413C7.196 15.021 7 14.55 7 14V13L2.2 8.2C2.15 8.5 2.104 8.8 2.062 9.1C2.02067 9.4 2 9.7 2 10C2 12.0167 2.66267 13.7833 3.988 15.3C5.31267 16.8167 6.98333 17.7 9 17.95ZM15.9 15.4C16.2333 15.0333 16.5333 14.6373 16.8 14.212C17.0667 13.7873 17.2877 13.3457 17.463 12.887C17.6377 12.429 17.771 11.9583 17.863 11.475C17.9543 10.9917 18 10.5 18 10C18 8.36667 17.546 6.875 16.638 5.525C15.7293 4.175 14.5167 3.2 13 2.6V3C13 3.55 12.8043 4.02067 12.413 4.412C12.021 4.804 11.55 5 11 5H9V7C9 7.28333 8.90433 7.52067 8.713 7.712C8.521 7.904 8.28333 8 8 8H6V10H12C12.2833 10 12.521 10.0957 12.713 10.287C12.9043 10.479 13 10.7167 13 11V14H14C14.4333 14 14.825 14.129 15.175 14.387C15.525 14.6457 15.7667 14.9833 15.9 15.4Z"
                          fill="#00308C"
                        />
                      </svg>
                      ${this.speakLanguages(response.languages)}
                    </div>`
                  : undefined}
                ${response.gender
                  ? html`<div slot="icon-with-text">
                      <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21.21 8.6C20.69 7.38 19.97 6.33 19.07 5.43C18.17 4.53 17.11 3.82 15.9 3.29C14.68 2.77 13.38 2.5 12 2.5C10.62 2.5 9.32 2.76 8.1 3.29C6.88 3.82 5.83 4.53 4.93 5.43C4.03 6.33 3.32 7.39 2.79 8.6C2.26 9.82 2 11.12 2 12.5C2 13.88 2.26 15.18 2.79 16.4C3.31 17.62 4.03 18.67 4.93 19.57C5.83 20.47 6.89 21.18 8.1 21.71C9.32 22.24 10.62 22.5 12 22.5C13.38 22.5 14.68 22.24 15.9 21.71C17.12 21.19 18.17 20.47 19.07 19.57C19.97 18.67 20.68 17.61 21.21 16.4C21.74 15.18 22 13.88 22 12.5C22 11.12 21.74 9.82 21.21 8.6ZM14.5 20.11C13.72 20.37 12.88 20.5 12 20.5C11.12 20.5 10.28 20.37 9.5 20.11C8.72 19.85 8 19.48 7.35 19C8 18.52 8.72 18.15 9.5 17.89C10.28 17.63 11.12 17.5 12 17.5C12.88 17.5 13.72 17.63 14.5 17.89C15.28 18.15 16 18.52 16.65 19C16 19.48 15.28 19.85 14.5 20.11ZM19.51 15.27C19.18 16.14 18.73 16.91 18.15 17.59C17.3 16.94 16.35 16.43 15.3 16.05C14.25 15.68 13.15 15.49 12 15.49C10.85 15.49 9.75 15.68 8.7 16.05C7.65 16.43 6.7 16.94 5.85 17.59C5.27 16.91 4.81 16.13 4.49 15.27C4.16 14.4 4 13.48 4 12.49C4 10.27 4.78 8.39 6.34 6.83C7.9 5.27 9.79 4.49 12 4.49C14.21 4.49 16.1 5.27 17.66 6.83C19.22 8.39 20 10.28 20 12.49C20 13.47 19.84 14.4 19.51 15.27Z"
                          fill="#00308C"
                        />
                        <path
                          d="M14.49 7.51C13.81 6.84 12.99 6.5 12 6.5C11.01 6.5 10.19 6.84 9.51 7.51C8.84 8.19 8.5 9.01 8.5 10C8.5 10.99 8.84 11.81 9.51 12.49C10.19 13.16 11.01 13.5 12 13.5C12.99 13.5 13.81 13.16 14.49 12.49C15.16 11.81 15.5 10.99 15.5 10C15.5 9.01 15.16 8.19 14.49 7.51ZM13.08 11.07C12.8 11.35 12.44 11.49 12 11.49C11.56 11.49 11.21 11.35 10.92 11.07C10.63 10.79 10.5 10.43 10.5 9.99C10.5 9.55 10.64 9.2 10.92 8.91C11.2 8.62 11.56 8.49 12 8.49C12.44 8.49 12.79 8.63 13.08 8.91C13.36 9.19 13.5 9.55 13.5 9.99C13.5 10.43 13.36 10.78 13.08 11.07Z"
                          fill="#428FEC"
                        />
                      </svg>
                      Gender: ${response.gender.toLowerCase()}
                    </div>`
                  : undefined}
                ${response.c_lGBTQIAHealth
                  ? html`<div slot="icon-with-text">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                      >
                        <path
                          fill="#E11129"
                          fill-rule="evenodd"
                          d="M11.382 5.5H4v-2h8.618l1 2H20v2h-7.618l-1-2Z"
                          clip-rule="evenodd"
                        />
                        <path
                          fill="#EC7D2E"
                          fill-rule="evenodd"
                          d="M11.382 7.5H4v-2h8.618l1 2H20v2h-7.618l-1-2Z"
                          clip-rule="evenodd"
                        />
                        <path
                          fill="#F8EC26"
                          fill-rule="evenodd"
                          d="M11.382 9.5H4v-2h8.618l1 2H20v2h-7.618l-1-2Z"
                          clip-rule="evenodd"
                        />
                        <path
                          fill="#32A530"
                          fill-rule="evenodd"
                          d="M11.382 11.5H4v-2h8.618l1 2H20v2h-7.618l-1-2Z"
                          clip-rule="evenodd"
                        />
                        <path
                          fill="#0047C7"
                          fill-rule="evenodd"
                          d="M11.382 13.5H4v-2h8.618l1 2H20v2h-7.618l-1-2Z"
                          clip-rule="evenodd"
                        />
                        <path
                          fill="#710E82"
                          fill-rule="evenodd"
                          d="M11.382 15.5H4v-2h8.618l1 2H20v2h-7.618l-1-2Z"
                          clip-rule="evenodd"
                        />
                        <path
                          fill="#00308C"
                          fill-rule="evenodd"
                          d="M4 20.5v-17h2v17H4Z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      LGBTQIA + Health
                    </div>`
                  : undefined}
              </tufts-clinical-trials-card>`
            : undefined}
        </tufts-side-by-side>
      </tufts-container>

      ${this.locationsArray && this.locationsArray.length > 0
        ? html` <tufts-container>
            <tufts-yext-location
              locations=${JSON.stringify(this.locationsArray)}
            >
            </tufts-yext-location>
          </tufts-container>`
        : null}
      <tufts-container>
        <div class="wrapper-icon-experience">
          <tufts-icon name="training" library="system" size="4rem"></tufts-icon>
          <p>Experience</p>
        </div>
        <div
          class="container-tufts-grid ${this.resizeController
            .currentBreakpointRange === 0
            ? 'isMobile'
            : null}"
        >
          <tufts-tab-group
            placement="top"
            background-color="transparent"
            container-width="full"
            padding="space-0"
          >
            <tufts-tab slot="nav" panel="training">
              Training &plus; education
            </tufts-tab>
            <tufts-tab-panel name="training">
              <p class="tab-subtitle">Training &plus; education</p>
              ${educationTypes.medical_schoool ||
              educationTypes.residency ||
              educationTypes.fellowship ||
              educationTypes.internship ||
              response.c_postgraduateType ||
              response.c_boardCertifiedSpecialties
                ? html`
                    <div class="tab-education">
                      ${this.generateEducationTemplate(
                        educationTypes.medical_schoool,
                        'Medical School'
                      )}
                      ${this.generateEducationTemplate(
                        educationTypes.internship,
                        'Internship'
                      )}
                      ${this.generateEducationTemplate(
                        educationTypes.residency,
                        'Residency'
                      )}
                      ${this.generateEducationTemplate(
                        educationTypes.fellowship,
                        'Fellowship'
                      )}
                      ${response.c_postgraduateType
                        ? html`
                            <div class="content-wrapper">
                              <span class="content-title"> Postgraduate</span>
                              <div class="content-details-wrapper">
                                <span>
                                  ${response.c_postgraduateType
                                    ? html` ${unsafeHTML(
                                        response.c_postgraduateType
                                          .map(postgrade => postgrade)
                                          .join('<br>')
                                      )}`
                                    : null}
                                </span>
                              </div>
                            </div>
                          `
                        : null}
                      ${response.c_boardCertifiedSpecialties
                        ? html`
                            <div class="content-wrapper">
                              <span class="content-title">
                                Board certifications</span
                              >
                              <div class="content-details-wrapper">
                                <span>
                                  ${unsafeHTML(
                                    response.c_boardCertifiedSpecialties
                                      ?.map(specialty => specialty)
                                      .join('<br>')
                                  )}
                                </span>
                              </div>
                            </div>
                          `
                        : null}
                    </div>
                  `
                : html`
                    <div class="tab-education">
                      Check back for more information on ${response.firstName}'s
                      training and education.
                    </div>
                  `}
            </tufts-tab-panel>
            <tufts-tab slot="nav" panel="honors">
              Honors &plus; awards
            </tufts-tab>

            <tufts-tab-panel name="honors">
              <p class="tab-subtitle">Honors &plus; awards</p>
              ${response.c_honorsAndAwards
                ? html` <div class="content-details-wrapper">
                    ${unsafeHTML(
                      ifDefined(
                        addTargetBlankToAnchorTags(response.c_honorsAndAwards)
                      )
                    )}
                  </div>`
                : html`
                    <div class="tab-education">
                      Check back for more information on ${response.firstName}'s
                      honors &plus; awards.
                    </div>
                  `}
            </tufts-tab-panel>

            <tufts-tab slot="nav" panel="publications">
              Research publications
            </tufts-tab>
            <tufts-tab-panel name="publications">
              <p class="tab-subtitle">Research publications</p>
              ${this.citationsHtml.length > 0 ||
              response.c_publicationsNationalPresentations
                ? html`
                    ${this.citationsHtml.length > 0 || response.c_oRCIDID
                      ? this.publicationAndNationalPresentationTemplate(
                          pubMedSearchUrl,
                          response.firstName
                        )
                      : html`
                          ${response.c_publicationsNationalPresentations
                            ? html` <div>
                                <div class="content-details-wrapper">
                                  ${unsafeHTML(
                                    ifDefined(
                                      addTargetBlankToAnchorTags(
                                        response.c_publicationsNationalPresentations
                                      )
                                    )
                                  )}
                                </div>
                              </div>`
                            : null}
                        `}
                  `
                : html`
                    <div class="tab-education">
                      Check back for more information on ${response.firstName}'s
                      research publications.
                    </div>
                  `}
            </tufts-tab-panel>
          </tufts-tab-group>
        </div>
      </tufts-container>

      ${response.c_biography || response.c_specialtyName
        ? html`<tufts-container>
            <tufts-side-by-side side="right" extra-space>
              <tufts-icon-with-paragraph
                slot="side-content"
                vertical-trigger-width="1080"
                heading-size="lg"
              >
                ${response.c_biography
                  ? html`<div slot="icon">
                        <svg
                          width="64"
                          height="64"
                          viewBox="0 0 64 64"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M32.0108 63.81C24.9008 63.81 18.0008 61.76 12.0508 57.9L13.2908 55.99C18.8608 59.62 25.3308 61.53 32.0008 61.53C38.6708 61.53 45.1408 59.61 50.7208 55.99L51.9608 57.9C46.0208 61.77 39.1208 63.81 32.0008 63.81H32.0108Z"
                            fill="#1A3A67"
                          ></path>
                          <path
                            d="M8.91922 53.38L6.69922 52.9C8.46922 44.77 14.0992 37.94 21.7692 34.63L22.4792 34.33L32.0092 43.86L41.5392 34.33L42.2492 34.63C49.9192 37.94 55.5492 44.77 57.3192 52.9L55.0992 53.38C53.5392 46.21 48.6892 40.15 42.0492 37.03L32.0092 47.07L21.9692 37.03C15.3292 40.15 10.4792 46.21 8.91922 53.38Z"
                            fill="#1A3A67"
                          ></path>
                          <path
                            d="M33.1411 45.46H30.8711V48.43H33.1411V45.46Z"
                            fill="#1A3A67"
                          ></path>
                          <path
                            d="M42.9302 45.46H40.6602V48.43H42.9302V45.46Z"
                            fill="#1A3A67"
                          ></path>
                          <path
                            d="M33.1411 51.96H30.8711V62.67H33.1411V51.96Z"
                            fill="#1A3A67"
                          ></path>
                          <path
                            d="M46.5695 48.44H37.0195V54.81H46.5695V48.44Z"
                            fill="#428FEC"
                          ></path>
                          <path
                            d="M31.9608 33.73C24.9808 33.73 19.3008 28.05 19.3008 21.07V18.31H21.5708V21.07C21.5708 26.8 26.2308 31.46 31.9608 31.46C37.6908 31.46 42.3508 26.8 42.3508 21.07V18.31H44.6208V21.07C44.6208 28.05 38.9408 33.73 31.9608 33.73Z"
                            fill="#1A3A67"
                          ></path>
                          <path
                            d="M31.9611 5.30995C38.9411 5.30995 44.6211 10.99 44.6211 17.97L44.6211 18.7299L42.3511 18.7299L42.3511 17.97C42.3511 12.24 37.6911 7.57995 31.9611 7.57995C26.2311 7.57995 21.5711 12.2399 21.5711 17.9699L21.5711 18.7299L19.3011 18.7299L19.3011 17.9699C19.3011 10.9899 24.9811 5.30995 31.9611 5.30995Z"
                            fill="#1A3A67"
                          ></path>
                        </svg>
                      </div>
                      <div slot="heading">
                        <h2>Meet ${response.firstName}</h2>
                      </div>`
                  : null}
                ${response.c_biography
                  ? html`<div slot="description">
                      <p>
                        ${unsafeHTML(
                          ifDefined(
                            addTargetBlankToAnchorTags(response.c_biography)
                          )
                        )}
                      </p>
                    </div>`
                  : null}
              </tufts-icon-with-paragraph>
              <div slot="main-content">
                <br />
                ${response.facebookPageUrl &&
                response.twitterHandle &&
                response.instagramHandle &&
                hiddenValue
                  ? html`
                      <tufts-clinical-trials-card border="">
                        <strong style="font-weight:bold" slot="icon-with-text">
                          SOCIAL MEDIA
                        </strong>
                        <div slot="icon-with-text">
                          <tufts-social-links
                            fb-link="${response.facebookPageUrl
                              ? `${response.facebookPageUrl}`
                              : undefined} "
                            tw-link="${response.twitterHandle
                              ? `https://www.twitter.com/${response.twitterHandle}`
                              : undefined} "
                            ig-link="${response.instagramHandle
                              ? `https://www.instagram.com/${response.instagramHandle}`
                              : undefined}"
                            li-link="${response.linkedInUrl
                              ? `${response.linkedInUrl}`
                              : undefined}"
                            screen-reader-text="connect on"
                            link-size="2rem"
                          >
                          </tufts-social-links>
                        </div>
                      </tufts-clinical-trials-card>
                    `
                  : undefined}
              </div>
            </tufts-side-by-side></tufts-container
          >`
        : undefined}
      ${this.videoInterviewTemplate(response)}
      ${this.dataFromClinicalTrials
        ? html`
            <tufts-container background-color="purple" padding="space-2xl">
              <div class="heading-with-link">
                <tufts-heading level-size="xl" class="block">
                  <h2>My clinical trials</h2>
                </tufts-heading>
                <a
                  class="arrow-right"
                  aria-label="See all clinical trials"
                  href="/research-clinical-trials/find-clinical-trials"
                  >See all clinical trials</a
                >
              </div>
              ${unsafeHTML(this.dataFromClinicalTrials)}
            </tufts-container>
          `
        : null}
      ${this.dataFromProfileNewsArticles
        ? html`
            <tufts-container background-color="transparent" padding="space-2xl">
              <div class="heading-with-link">
                <tufts-heading level-size="xl" class="block">
                  <h2>In the news</h2>
                </tufts-heading>
                <a
                  class="arrow-right"
                  aria-label="See all related news"
                  href="/about-us/news/search"
                  >See all news</a
                >
              </div>
              ${unsafeHTML(this.dataFromProfileNewsArticles)}
            </tufts-container>
          `
        : null}
      ${response.question_ratings && response.question_ratings.length > 0
        ? html` <tufts-container
            background-color="transparent"
            padding="space-l"
            id="rating"
          >
            <div class="container-comment-rating">
              <tufts-heading level-size="xl" tabindex="-1">
                <h2>Patient reviews</h2>
              </tufts-heading>
              <div
                class="container-tufts-ratings ${this.resizeController
                  .currentBreakpointRange === 0
                  ? 'isMobile'
                  : null}"
              >
                <div>
                  <p>
                    All reviews come from patients who have seen this doctor.
                    <br />
                    We remove any personal health information to protect privacy
                    and filter out comments that aren't related to the care
                    provided.
                  </p>
                  <div class="show-rating-container">
                    ${response.question_ratings.length > 0
                      ? html`
                          ${response.question_ratings.map(
                            ra => html`
                              <tufts-rating score="${ra.rating}">
                                <div class="rating-text">${ra.label}</div>
                              </tufts-rating>
                            `
                          )}
                        `
                      : null}
                  </div>
                </div>
                <div>
                  <tufts-heading level-size="sm">
                    <h3>
                      ${response.rating_count} rating |
                      ${response.comment_count} comments
                    </h3>
                  </tufts-heading>
                  ${this.dataComments && this.dataComments.length > 0
                    ? html`
                        ${repeat(
                          this.dataComments,
                          (_, index) => index,
                          ra => html`
                            <tufts-rating
                              score="${ra.rating}"
                              variant="comments"
                              date="${ra.review_date}"
                            >
                              <div class="rating-text">${ra.comment}</div>
                            </tufts-rating>
                            <tufts-hr></tufts-hr>
                          `
                        )}
                        ${this.currentPage < this.totalPages
                          ? html`
                              <button
                                type="button"
                                class="btn medium link"
                                @click="${this.loadMoreComments}"
                              >
                                Load more
                              </button>
                            `
                          : null}
                      `
                    : html`
                        <p>
                          ${response.firstName} does not have any comments at
                          this time.
                        </p>
                      `}
                </div>
              </div>
            </div>
          </tufts-container>`
        : null}
    `;
  }
  generateExpertiseTemplate(
    response: EntityDataStructure
  ): TemplateResult | null {
    const expertise = response.c_expertise;
    const specialtyName = response.c_specialtyName;

    if (expertise || specialtyName) {
      const title = 'Expertise';
      const items = expertise || specialtyName || [];

      return html`
        <div slot="expertise">
          <div class="body-container">
            <strong>${title}</strong>
            ${items.map(item => html`${item}<br />`)}
          </div>
        </div>
      `;
    }

    return null;
  }

  loadMoreComments() {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      this.fetchRatings(this.currentPage);
    }
  }

  async getUrlIDFromURLParams() {
    if (!this.urlID) {
      const urlParams = new URLSearchParams(window.location.search);
      this.urlID = urlParams.get('yext') ?? '';
    }
    await this.getIDFromQuery(this.urlID);
  }

  async getIDFromQuery(url_id: string) {
    const params = new URLSearchParams();
    params.set('api_key', this.apiKey);
    params.set('v', this.apiVersion);
    const filters = JSON.stringify({ c_url_id: { $eq: url_id } });
    params.set('filter', filters);
    const url = `https://cdn.yextapis.com/v2/accounts/me/entities?${params.toString()}`;

    try {
      const response = await fetch(url);
      const doctorResponse: {
        meta: {};
        response: verticalSearchResponseStructure;
      } = await response.json();

      if (doctorResponse.response.entities.length <= 0) {
        console.error('no matching entities found.');
        return;
      }

      doctorResponse.response.entities.forEach(
        (doctor: EntityDataStructure) => {
          this.entityId = doctor.meta.id;
        }
      );
    } catch (error) {
      console.error(error);
    }
  }

  prepareRequestUrl(id: string) {
    const params = new URLSearchParams();
    params.set('api_key', this.apiKey);
    params.set('v', this.apiVersion);

    // encode id before sending it to url
    const encodedId = encodeURIComponent(id);

    return `${this.urlBase}/${encodedId}?${params.toString()}`;
  }

  templateCTA(response: EntityDataStructure) {
    return html`
      ${response.acceptingNewPatients
        ? html`
            <tufts-icon-link>
              <div slot="icon">
                <tufts-icon
                  library="system"
                  name="calendar"
                  size="100%"
                ></tufts-icon>
              </div>
              <div slot="link">
                <a href="/get-care/request-appointment#request"
                  >Request an appointment</a
                >
              </div>
            </tufts-icon-link>
          `
        : null}
      <tufts-icon-link>
        <div slot="icon">
          <tufts-icon library="system" name="profile" size="100%"></tufts-icon>
        </div>
        <div slot="link">
          <a href="/refer-a-patient">Refer a patient</a>
        </div>
      </tufts-icon-link>
      <tufts-icon-link>
        <div slot="icon">
          <tufts-icon library="system" name="download" size="100%"></tufts-icon>
        </div>
        <div slot="link">
          <a href=${this.createVcard(response)} download="${response.name}.vcf">
            Save contact
          </a>
        </div>
      </tufts-icon-link>
      <tufts-icon-link>
        <div slot="icon">
          <tufts-icon
            library="system"
            name="snowflake"
            size="100%"
          ></tufts-icon>
        </div>
        <div slot="link">
          <a href="/accepted-insurance">View accepted insurance</a>
        </div>
      </tufts-icon-link>
      <tufts-icon-link>
        <div slot="icon">
          <tufts-icon library="system" name="myChart" size="100%"></tufts-icon>
        </div>
        <div slot="link">
          <a
            href="https://www.mytuftsmed.org/MychartPRD/Authentication/Login"
            target="_blank"
            rel="noopener noreferrer"
            >Patient portal (myTuftsMed)</a
          >
        </div>
      </tufts-icon-link>
    `;
  }

  createVcard(contact: EntityDataStructure) {
    const categories = contact.c_specialtyName?.join(', ');
    // Create vCard without indentation and with proper line endings
    let vcard = 'BEGIN:VCARD\r\n';
    vcard += 'VERSION:3.0\r\n';
    // Ensure lastName and firstName exist before using them
    vcard += `N:${contact.lastName || ''};${contact.firstName || ''};;;\r\n`;
    // Escape special characters like commas, semicolons, and backslashes according to vCard spec
    const fullName =
      contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`;
    vcard += `FN:${fullName.replace(/[,;\\]/g, '\\$&')}\r\n`;

    if (contact.mainPhone) {
      // Remove any non-numeric characters from phone number
      const cleanPhone = contact.mainPhone.replace(/[^0-9+]/g, '');
      vcard += `TEL;TYPE=WORK,VOICE:${cleanPhone}\r\n`;
    }

    if (categories) {
      vcard += `NOTE:${categories.replace(/[,;\\]/g, '\\$&')}\r\n`;
    }

    vcard += 'ORG:Tufts Medicine\r\n';
    vcard += 'END:VCARD';

    // Create a Blob object from the vcard string
    const blob = new Blob([vcard], { type: 'text/vcard' });
    return URL.createObjectURL(blob);
  }

  videoInterviewTemplate(response: EntityDataStructure) {
    return response.c_videoInterviewURL
      ? html`
          <tufts-container background-color="transparent">
            <tufts-video-lite
              video-link="${response.c_videoInterviewURL}"
            ></tufts-video-lite>
          </tufts-container>
        `
      : null;
  }

  async fetchAdditionalProfileLocations(
    response: verticalSearchResponseStructure
  ) {
    if (response.c_additionalProfiles?.length > 0) {
      // Map each additional profile to a fetch Promise
      const fetchPromises = response.c_additionalProfiles.map(
        additionalProfile => {
          const params = new URLSearchParams();
          params.set('api_key', this.apiKey);
          params.set('v', this.apiVersion);

          // Encode the profile's urlID before constructing the URL
          const encodedId = encodeURIComponent(additionalProfile);
          const url = `${this.urlHref}/${this.accountId}/entities/${encodedId}?${params.toString()}`;

          // Return the fetch Promise
          return fetch(url);
        }
      );

      // Execute all fetch Promises in parallel
      Promise.all(fetchPromises)
        .then(async responses => {
          // Map each response to its JSON data
          const jsonDataPromises = responses.map(response => response.json());
          const additionalProfileDataArray =
            await Promise.all(jsonDataPromises);

          // Add each additional profile's location
          additionalProfileDataArray.forEach(additionalProfileData => {
            this.addLocation(additionalProfileData.response);
          });
          this.requestUpdate();
        })
        .catch(error => console.error('Error:', error));
    }

    if (response.npi) {
      this.npiProvider = response.npi;
      try {
        // Construct the URL for the rating API
        const ratingResponse = await fetch(
          `https://ratings.md/api/v1/widget/profiles/${response.npi}?account=tufts-medicine&id_type=npi&page_size=5&page_num=1`
        );

        // Parse the JSON response into a RatingResponse object
        const ratingData: RatingResponse = await ratingResponse.json();

        // If the ratingData contains a message, it indicates an error, so return early
        if (ratingData.message) {
          return;
        } else {
          response.rating = ratingData.rating;
          response.question_ratings = ratingData.question_ratings;
          response.rating_count = ratingData.rating_count;
          response.comment_count = ratingData.comment_count;
          this.dataComments = ratingData.comments.data || [];
          this.totalPages = ratingData.comments?.meta?.total_pages || 1;
        }
      } catch (error) {
        // Error is intentionally unhandled as there's no user-facing action
        // or recovery to be done if this service fails.
      }
    }

    this.requestUpdate();
  }

  async fetchRatings(pageNum: number) {
    if (this.npiProvider) {
      try {
        // Construct the URL for the rating API
        const ratingResponse = await fetch(
          `https://ratings.md/api/v1/widget/profiles/${this.npiProvider}?account=tufts-medicine&id_type=npi&page_size=5&page_num=${pageNum}`
        );

        // Parse the JSON response into a RatingResponse object
        const ratingData: RatingResponse = await ratingResponse.json();

        // If the ratingData contains a message, it indicates an error, so return early
        if (ratingData.message) {
          return;
        } else {
          // Append new comments to the existing array
          this.dataComments = [
            ...this.dataComments,
            ...(ratingData.comments?.data || []),
          ];
        }
      } catch (error) {
        // Error is intentionally unhandled as there's no user-facing action
        // or recovery to be done if this service fails.
      }
    }
  }

  async fetchProfileNewsArticles(response: EntityDataStructure) {
    // Check if there is meta.uid data
    if (response.meta.uid) {
      const url = `${this.currentDomainURL}/tufts-api/news-articles-by-provider-yext-id/${response.meta.uid}`;

      try {
        // Fetch the additional profile's data from the URL
        const response = await fetch(url);
        const profileData = await response.json();
        this.dataFromProfileNewsArticles = profileData.data;
      } catch (error) {
        console.error('Error:', error);
      }
    }
  }

  async fetchProfileClinicalTrials(response: EntityDataStructure) {
    // Check if there is meta.uid data
    if (response.meta.uid) {
      const url = `${this.currentDomainURL}/tufts-api/clinical-trials-by-provider-yext-id/${response.meta.uid}`;

      try {
        // Fetch the additional profile's data from the URL
        const data = await fetch(url);
        const profileData = await data.json();
        this.dataFromClinicalTrials = profileData.data;
      } catch (error) {
        console.error('Error:', error);
      }
    }
  }

  refresh() {
    this._apiTask.run();
  }

  setOpenSchedulingData(
    npi: string,
    visitTypeId: [string],
    additionalDepartmentId: [string]
  ) {
    const openSchedulingElement = this.querySelector(
      '[slot="open-scheduling"]'
    ) as HTMLElement;

    if (openSchedulingElement) {
      const hostname = window.location.hostname;

      // Map the hostname to the corresponding API key
      const envConfigurations = new Map([
        [
          'tufts.devcloud.fayze2.com',
          {
            apiKey: 'dkIQPNQ93054164',
            env: 'dev',
          },
        ],
        [
          'tuftsmedicinedev.prod.acquia-sites.com',
          {
            apiKey: 'xtCt60101125551',
            env: 'dev',
          },
        ],
        [
          'tufts.docksal.site',
          {
            apiKey: 'xaYfQeAwwsdzmSw',
            env: 'dev',
          },
        ],
        [
          'tuftsmedicinestg.prod.acquia-sites.com',
          {
            apiKey: 'BmTErgi31745804',
            env: 'dev',
          },
        ],
        [
          'tuftsmedicine.prod.acquia-sites.com',
          {
            apiKey: 'hPTah7283327269',
            env: 'prod',
          },
        ],
        [
          'www.tuftsmedicine.org',
          {
            apiKey: 'dBYxa0718191158',
            env: 'prod',
          },
        ],
      ]);

      // Get the API key based on the hostname
      const apiKey =
        envConfigurations.get(hostname)?.apiKey || 'xaYfQeAwwsdzmSw';

      let dataUrl: string;
      let scriptUrl: string;

      // Run this once and only if there's an apiKey for the current environment
      if (apiKey) {
        if (envConfigurations.get(hostname)?.env === 'prod') {
          dataUrl = 'https://www.mytuftsmed.org/MychartPRD';
          scriptUrl =
            'https://www.mytuftsmed.org/MychartPRD/Scripts/lib/Widget/widget_sdk.js';
        } else {
          dataUrl = 'https://mychart-np.well-net.org/MyChartPOC';
          scriptUrl =
            'https://mychart-np.well-net.org/MyChartPOC/Scripts/lib/Widget/widget_sdk.js';
        }

        openSchedulingElement.dataset.url = dataUrl;
        openSchedulingElement.dataset.apikey = apiKey;
        openSchedulingElement.dataset.widgetType = 'openscheduling';

        if (npi) {
          openSchedulingElement.dataset.additionalparamsId = npi;
        }
        if (visitTypeId) {
          openSchedulingElement.dataset.additionalparamsVt =
            visitTypeId.join(',');
        }
        if (additionalDepartmentId) {
          openSchedulingElement.dataset.additionalparamsDept =
            additionalDepartmentId.join(',');
        }

        const script = document.createElement('script');
        script.src = scriptUrl;
        document.head.appendChild(script);

        // The MyCharts script expects to load during the initial page load and will wait for window.load events.
        //  But, since we add it later, those would normally not happen at this point.
        script.onload = () => {
          window.dispatchEvent(new Event('load'));
        };

        this.openScheduling = true;
        const urlParams = new URLSearchParams(window.location.search);
        this.openSchedulingFromSearch =
          urlParams.get('open-scheduling') !== null;
      } else {
        console.warn(
          'Open Scheduling - no API key found for hostname: ' + hostname
        );
      }
    }
  }

  render(): TemplateResult {
    return html`
      ${this.entityId
        ? html` ${this._apiTask.render({
            pending: () =>
              html`<div class="lds-ring">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>`,
            complete: data => html` ${this.displayEntity(data.response)} `,
            error: error => html`${error}`,
          })}`
        : html`<h2>No entity loaded yet</h2>`}
    `;
  }

  speakLanguages(languages: string[]): string {
    if (languages.length === 1) {
      return 'Other languages: ' + languages[0];
    } else if (languages.length === 2) {
      return 'Other languages: ' + languages.join(' and ');
    } else if (languages.length > 2) {
      const lastLanguage = languages[languages.length - 1];
      return (
        'Other languages: ' +
        languages.slice(0, -1).join(', ') +
        ' and ' +
        lastLanguage
      );
    } else {
      return '';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove the event listener when the component is disconnected from the DOM
    window.removeEventListener('load', this.handleLoadEvent);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-yext-item': TuftsYextItem;
  }
}
